
import { env } from '@/lib/env';
import { WebSpeechTTSProvider } from './WebSpeechTTSProvider';
import {
  type TTSProvider,
  type QueueItem,
  MAX_PREFETCH,
  PER_REQUEST_TIMEOUT_MS,
} from './tts-types';

// Re-export the public TTS surface so existing import paths stay identical.
export { WebSpeechTTSProvider };
export type { TTSProvider };

export class ElevenLabsTTSProvider implements TTSProvider {
  onStart?: () => void;
  onEnd?: () => void;

  private audioContext: AudioContext | null = null;
  private currentSource: AudioBufferSourceNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private gainNode: GainNode | null = null;
  private abortController: AbortController | null = null;
  private speaking = false;
  private token: string | null = null;
  private fallback = new WebSpeechTTSProvider();
  private playbackId = 0;
  private endedForPlaybackId = -1;

  getAnalyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  /** Expose AudioContext so StreamingTTSProvider can share it */
  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  /** Expose token for StreamingTTSProvider */
  getToken(): string | null {
    return this.token;
  }

  setToken(token: string | null) {
    this.token = token;
  }

  /**
   * Centralized cleanup for a completed/stopped playback session.
   * Guards against double-fire: onEnd fires at most once per playbackId.
   */
  private finishPlayback(id: number): void {
    if (this.endedForPlaybackId === id) return; // already fired for this playback
    this.endedForPlaybackId = id;
    this.currentSource = null;
    this.analyserNode = null;
    this.gainNode = null;
    this.speaking = false;
    this.onEnd?.();
  }

  async prepare(): Promise<void> {
    // Recreate AudioContext if it was closed or lost (iOS backgrounding, OS reclaim)
    if (!this.audioContext || this.audioContext.state === 'closed') {
      try { this.audioContext?.close(); } catch (_) { /* already closed */ }
      this.audioContext = new AudioContext();
    }
    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (err) {
        // resume() failed — recreate the context entirely
        console.warn('[TTS] AudioContext.resume() failed, recreating:', err);
        try { this.audioContext.close(); } catch (_) { /* ignore */ }
        this.audioContext = new AudioContext();
      }
    }
  }

  async speak(text: string): Promise<void> {
    this.stop();
    const myPlaybackId = ++this.playbackId;
    this.speaking = true;
    this.abortController = new AbortController();

    await this.prepare();

    try {
      const url = `${env.apiBaseUrl}/tts/synthesize`;
      console.log('[TTS] Requesting ElevenLabs:', url, 'text length:', text.length);
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ text, profile: 'voice_reply' }),
        signal: this.abortController.signal,
      });

      console.log('[TTS] Response:', response.status, response.headers.get('content-type'));
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[TTS] Error body:', errorText);
        throw new Error(`TTS request failed: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      console.log('[TTS] Received audio:', arrayBuffer.byteLength, 'bytes');
      if (!this.speaking) return; // stopped while fetching

      // If context still isn't running after prepare(), bail to WebSpeech fallback
      if (!this.audioContext || this.audioContext.state !== 'running') {
        throw new Error(`AudioContext in ${this.audioContext?.state ?? 'null'} state after prepare`);
      }

      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      console.log('[TTS] Decoded audio:', audioBuffer.duration.toFixed(1), 'seconds');
      if (!this.speaking) return;

      this.onStart?.();

      await new Promise<void>((resolve) => {
        const source = this.audioContext!.createBufferSource();
        source.buffer = audioBuffer;

        // Audio graph: source → gain → analyser → destination
        this.gainNode = this.audioContext!.createGain();
        this.analyserNode = this.audioContext!.createAnalyser();
        this.analyserNode.fftSize = 512;
        this.analyserNode.smoothingTimeConstant = 0;

        source.connect(this.gainNode);
        this.gainNode.connect(this.analyserNode);
        this.analyserNode.connect(this.audioContext!.destination);

        this.currentSource = source;

        source.onended = () => {
          this.finishPlayback(myPlaybackId);
          resolve();
        };

        // Small lookahead so the audio device finishes warming up before
        // the first samples are scheduled — prevents clipping the first
        // ~20–80ms of speech on a recently-resumed AudioContext.
        source.start(this.audioContext!.currentTime + 0.06);
      });
    } catch (err) {
      this.speaking = false;
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('[TTS] Aborted');
        return; // stopped intentionally
      }
      console.warn('[TTS] ElevenLabs failed, falling back to WebSpeech:', err);
      this.fallback.onStart = this.onStart;
      this.fallback.onEnd = this.onEnd;
      return this.fallback.speak(text);
    }
  }

  duck(): void {
    if (this.gainNode && this.audioContext) {
      const now = this.audioContext.currentTime;
      this.gainNode.gain.cancelScheduledValues(now);
      this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now);
      this.gainNode.gain.linearRampToValueAtTime(0.15, now + 0.08);
    }
  }

  unduck(): void {
    if (this.gainNode && this.audioContext) {
      const now = this.audioContext.currentTime;
      this.gainNode.gain.cancelScheduledValues(now);
      this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now);
      this.gainNode.gain.linearRampToValueAtTime(1.0, now + 0.12);
    }
  }

  stop(): void {
    const id = this.playbackId;
    const wasActive = this.speaking || this.currentSource !== null || this.abortController !== null;

    this.speaking = false;
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    if (this.currentSource && this.audioContext && this.gainNode) {
      // Soft-stop: ramp gain to zero over 70ms then stop source
      try {
        const now = this.audioContext.currentTime;
        this.gainNode.gain.cancelScheduledValues(now);
        this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now);
        this.gainNode.gain.linearRampToValueAtTime(0.0001, now + 0.07);
        this.currentSource.stop(now + 0.08);
      } catch (_) { /* ignore */ }
      this.currentSource = null;
    } else if (this.currentSource) {
      try { this.currentSource.stop(); } catch (_) { /* ignore */ }
      this.currentSource = null;
    }
    this.analyserNode = null;
    this.gainNode = null;

    // Increment playbackId so the stale onended from soft-stop (80ms later) is a no-op
    ++this.playbackId;

    if (wasActive) {
      this.finishPlayback(id);
    }
  }

  isSpeaking(): boolean {
    return this.speaking;
  }

  dispose(): void {
    this.stop();
    this.fallback.dispose();
    try { this.audioContext?.close(); } catch (_) { /* already closed */ }
    this.audioContext = null;
  }
}

// ---------------------------------------------------------------------------
// StreamingTTSProvider — sentence-by-sentence TTS with audio queue
// ---------------------------------------------------------------------------

export class StreamingTTSProvider {
  onFirstAudioStart?: () => void;
  onAllDone?: () => void;
  /** Called when streaming fails — consumer should set React state for fallback */
  onStreamingFailed?: () => void;
  /** Called when the audio queue drains mid-turn (e.g., preamble done, tool
   *  running, awaiting more text). Use to drop the orb back to a thinking
   *  state. Fires at most once between resumes. */
  onAudioPause?: () => void;
  /** Called when audio playback resumes after onAudioPause (post-tool text
   *  arrived). Use to flip the orb back to speaking. */
  onAudioResume?: () => void;

  private parentProvider: ElevenLabsTTSProvider;
  private queue: QueueItem[] = [];
  private playIndex = 0;
  private _turnId = 0;
  private finished = false;
  private hasStartedPlaying = false;
  private _streamingFailed = false;
  private allDoneFired = false;

  // Per-turn audio graph (reuses parent's AudioContext — caller must not close it)
  private gainNode: GainNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private currentSource: AudioBufferSourceNode | null = null;
  private isPlaying = false;
  private resumeAttempted = false;
  // True if the AudioContext was just resumed (turn start, mid-turn resume
  // after backgrounding, Bluetooth handoff, etc.) — the next source.start
  // gets a small lookahead so the audio device has time to drain its first
  // output buffer before our samples land. Reset after one use.
  private justResumed = true;
  // True between an onAudioPause fire and the next onAudioResume fire, so
  // we don't double-fire the pause and we know to fire resume on next play.
  private isPaused = false;

  constructor(parentProvider: ElevenLabsTTSProvider) {
    this.parentProvider = parentProvider;
  }

  get turnId(): number {
    return this._turnId;
  }

  get streamingFailed(): boolean {
    return this._streamingFailed;
  }

  getAnalyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  private getAudioContext(): AudioContext | null {
    return this.parentProvider.getAudioContext();
  }

  private getToken(): string | null {
    return this.parentProvider.getToken();
  }

  /** Returns true if a turn is in progress (started but not cancelled/finished-and-drained) */
  isTurnActive(): boolean {
    return this._turnId > 0 && !this.allDoneFired;
  }

  startTurn(): number {
    // 1. Cancel previous turn first (operates on old state)
    this.cancelAll();
    // 2. Increment turnId so stale callbacks are rejected
    const id = ++this._turnId;
    // 3. Create per-turn audio nodes
    const ctx = this.getAudioContext();
    if (ctx) {
      this.gainNode = ctx.createGain();
      this.analyserNode = ctx.createAnalyser();
      this.analyserNode.fftSize = 512;
      this.analyserNode.smoothingTimeConstant = 0;
      this.gainNode.connect(this.analyserNode);
      this.analyserNode.connect(ctx.destination);
    }
    // 4. Reset state
    this.finished = false;
    this.hasStartedPlaying = false;
    this._streamingFailed = false;
    this.allDoneFired = false;
    this.resumeAttempted = false;
    this.justResumed = true; // first source.start of a turn gets the warmup pad
    this.isPaused = false;
    this.queue = [];
    this.playIndex = 0;
    this.isPlaying = false;

    return id;
  }

  addSentence(text: string, forTurnId: number): void {
    if (forTurnId !== this._turnId) return; // stale
    if (!this.gainNode) return; // post-cancel, pre-startTurn — nothing to play into
    if (!text.trim()) return; // empty after cleanForSpeech

    this.queue.push({ text, status: 'pending', buffer: undefined, abort: undefined });
    this.maybeStartFetch();
  }

  finishTurn(): void {
    this.finished = true;
    // Only fire immediately if nothing was queued or everything already completed.
    // Items still in pending/fetching/ready/playing will trigger fireAllDone
    // from onended or maybePlayNext when they complete.
    if (this.queue.length === 0 || this.checkAllDone()) {
      this.fireAllDone();
    }
  }

  cancelAll(): void {
    // Mark turn dead so isTurnActive() reports false to outside callers.
    // Prevents addSentence/feedTextChunk from queueing into the torn-down graph.
    this.allDoneFired = true;

    const ctx = this.getAudioContext();

    // 1. Stop currently playing audio with soft ramp + disconnect
    if (this.currentSource) {
      if (ctx && this.gainNode) {
        try {
          const now = ctx.currentTime;
          this.gainNode.gain.cancelScheduledValues(now);
          this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now);
          this.gainNode.gain.linearRampToValueAtTime(0.0001, now + 0.07);
          this.currentSource.stop(now + 0.08);
        } catch (_) { /* ignore */ }
      } else {
        try { this.currentSource.stop(); } catch (_) { /* ignore */ }
      }
      try { this.currentSource.disconnect(); } catch (_) { /* ignore */ }
      this.currentSource = null;
    }

    // 2. Abort all in-flight fetches + free buffers
    for (const item of this.queue) {
      if (item.abort) {
        item.abort.abort();
        item.abort = undefined;
      }
      item.buffer = undefined; // GC
    }

    // 3. Clear queue and state
    this.queue = [];
    this.playIndex = 0;
    this.isPlaying = false;
    this.finished = false;
    this.isPaused = false;

    // 4. Disconnect audio nodes
    if (this.gainNode) {
      try { this.gainNode.disconnect(); } catch (_) { /* ignore */ }
      this.gainNode = null;
    }
    if (this.analyserNode) {
      try { this.analyserNode.disconnect(); } catch (_) { /* ignore */ }
      this.analyserNode = null;
    }
  }

  duck(): void {
    const ctx = this.getAudioContext();
    if (this.gainNode && ctx) {
      const now = ctx.currentTime;
      this.gainNode.gain.cancelScheduledValues(now);
      this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now);
      this.gainNode.gain.linearRampToValueAtTime(0.15, now + 0.08);
    }
  }

  unduck(): void {
    const ctx = this.getAudioContext();
    if (this.gainNode && ctx) {
      const now = ctx.currentTime;
      this.gainNode.gain.cancelScheduledValues(now);
      this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now);
      this.gainNode.gain.linearRampToValueAtTime(1.0, now + 0.12);
    }
  }

  private checkAllDone(): boolean {
    return this.queue.length > 0 && this.queue.every(item => item.status === 'done');
  }

  /** Guard: fire onAllDone at most once per turn */
  private fireAllDone(): void {
    if (this.allDoneFired) return;
    this.allDoneFired = true;
    this.onAllDone?.();
  }

  private maybeStartFetch(): void {
    const ctx = this.getAudioContext();
    if (!ctx || ctx.state === 'closed') return;

    const inFlight = this.queue.filter(i => i.status === 'fetching').length;
    const available = MAX_PREFETCH - inFlight;
    if (available <= 0) return;

    const pendingItems = this.queue.filter(i => i.status === 'pending');
    for (let i = 0; i < Math.min(available, pendingItems.length); i++) {
      this.fetchSentence(pendingItems[i]);
    }
  }

  private async fetchSentence(item: QueueItem, isRetry = false): Promise<void> {
    const myTurnId = this._turnId;
    item.status = 'fetching';
    const abort = new AbortController();
    item.abort = abort;

    const timeoutId = setTimeout(() => abort.abort(), PER_REQUEST_TIMEOUT_MS);

    try {
      const url = `${env.apiBaseUrl}/tts/synthesize`;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = this.getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ text: item.text, profile: 'voice_reply' }),
        signal: abort.signal,
      });

      if (myTurnId !== this._turnId) return;

      if (response.status === 429 && !isRetry) {
        // Rate limited — wait then check staleness before mutating
        await new Promise(r => setTimeout(r, 500));
        if (myTurnId !== this._turnId) return;
        item.status = 'pending';
        item.abort = undefined;
        return this.fetchSentence(item, true);
      }

      if (!response.ok) {
        throw new Error(`TTS request failed: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      if (myTurnId !== this._turnId) return;

      const audioContext = this.getAudioContext();
      if (!audioContext || audioContext.state === 'closed') return;

      // Try to resume suspended context (iOS backgrounding)
      if (audioContext.state === 'suspended') {
        try { await audioContext.resume(); } catch (_) { /* ignore */ }
        if (myTurnId !== this._turnId) return;
        if (audioContext.state !== 'running') return;
        this.justResumed = true; // device needs warmup before next source.start
      }

      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      if (myTurnId !== this._turnId) return;

      item.buffer = audioBuffer;
      item.status = 'ready';
      item.abort = undefined;

      this.maybePlayNext();
    } catch (err) {
      if (myTurnId !== this._turnId) return;
      if (err instanceof Error && err.name === 'AbortError') {
        // Timeout abort (not user-cancel via cancelAll which replaces the queue).
        // Mark as done so it doesn't block a prefetch slot.
        if (item.status === 'fetching') {
          item.status = 'done';
          item.buffer = undefined;
          item.abort = undefined;
        }
        return;
      }

      console.warn('[StreamTTS] Sentence failed, skipping:', item.text.slice(0, 30), err);
      item.status = 'done';
      item.buffer = undefined;
      item.abort = undefined;

      // Only mark as failed if first sentence fails and nothing has played yet
      if (this.queue.indexOf(item) === 0 && !this.hasStartedPlaying) {
        this._streamingFailed = true;
        this.onStreamingFailed?.();
      }

      this.maybePlayNext();
    } finally {
      clearTimeout(timeoutId);
      this.maybeStartFetch();
    }
  }

  private maybePlayNext(): void {
    if (this.isPlaying) return;

    while (this.playIndex < this.queue.length) {
      const item = this.queue[this.playIndex];
      if (item.status === 'ready') break;
      if (item.status === 'done') { this.playIndex++; continue; }
      return; // fetching or pending — wait
    }

    if (this.playIndex >= this.queue.length) {
      if (this.finished && this.checkAllDone()) this.fireAllDone();
      return;
    }

    const item = this.queue[this.playIndex];
    const ctx = this.getAudioContext();
    if (!ctx || !this.gainNode) return;

    // Try to resume suspended context before playing (once per turn to avoid infinite loop)
    if (ctx.state === 'suspended' && !this.resumeAttempted) {
      this.resumeAttempted = true;
      const tid = this._turnId;
      ctx.resume().then(() => {
        if (tid !== this._turnId) return;
        this.justResumed = true; // device needs warmup before next source.start
        this.maybePlayNext();
      }).catch(() => {});
      return;
    }
    if (ctx.state !== 'running') return;

    item.status = 'playing';
    this.isPlaying = true;

    const source = ctx.createBufferSource();
    source.buffer = item.buffer!;
    source.connect(this.gainNode);
    this.currentSource = source;

    const myTurnId = this._turnId;

    source.onended = () => {
      source.disconnect();
      item.buffer = undefined;

      // If the turn was already cancelled (cancelAll sets allDoneFired)
      // ignore this late onended — otherwise the queue-drained branch
      // below fires onAudioPause and the orb flips to thinking right
      // after the user explicitly hit stop.
      if (this.allDoneFired) return;

      // Check turnId BEFORE mutating shared state — cancelAll may have
      // already reset the queue/playIndex for the next turn. Mutating
      // here would corrupt the new turn's state.
      if (myTurnId !== this._turnId) return;

      item.status = 'done';
      this.currentSource = null;
      this.isPlaying = false;
      this.playIndex++;

      this.maybeStartFetch();
      // Use queueMicrotask to avoid recursive call stack growth
      queueMicrotask(() => {
        if (myTurnId !== this._turnId) return;
        this.maybePlayNext();
        if (this.finished && this.checkAllDone()) {
          this.fireAllDone();
        } else if (!this.isPlaying && !this.finished && !this.isPaused) {
          // Audio queue drained mid-turn — preamble finished and we're
          // waiting for tool result / more text. Drop the orb back to a
          // thinking state until the next sentence starts playing.
          this.isPaused = true;
          this.onAudioPause?.();
        }
      });
    };

    if (!this.hasStartedPlaying) {
      this.hasStartedPlaying = true;
      this.onFirstAudioStart?.();
    } else if (this.isPaused) {
      // Audio is resuming after a mid-turn drain (post-tool answer text
      // arrived). Flip the orb back to speaking.
      this.isPaused = false;
      this.onAudioResume?.();
    }

    // After a fresh AudioContext resume (turn start, mid-turn iOS background
    // resume, Bluetooth handoff), pad the schedule so the audio device has
    // time to drain its first output buffer before our samples land — fixes
    // first-words clipping. Tight back-to-back sentences within a running
    // device don't need the offset (and adding it would create audible gaps).
    const startAt = this.justResumed
      ? ctx.currentTime + 0.06
      : ctx.currentTime;
    this.justResumed = false;
    source.start(startAt);
  }

  dispose(): void {
    this.cancelAll();
  }
}
